import type { SalaryPaymentLineDto } from '@bbl/validation';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Pause,
  CheckCircle2,
  Play,
  RefreshCw,
  Sliders,
  UserCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError, openApiDocument } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { StatusTransitionButton } from '../components/shared/StatusTransitionButton';
import { SalaryStatusBadge } from '../components/finance/StatusBadge';
import { SalaryMarkPaidDrawer } from '../components/finance/SalaryMarkPaidDrawer';
import { AdjustSalaryAmountsDrawer } from '../components/finance/AdjustSalaryAmountsDrawer';
import { formatDate, formatINR } from '../lib/format';
import {
  cycleLabel,
  type SalaryCycle,
  type SalaryPayment,
  type SalaryPaymentJoined,
} from '../components/finance/types';

export const Route = createFileRoute('/_authed/finance/salaries/payments/$paymentId')({
  component: SalaryPaymentDetailPage,
});

// ---------------------------------------------------------------------------
// Wire shapes — match the enriched detail GET documented in §4.18. The page
// must tolerate the older `{ payment, cycle, teacher, manager }` shape too
// (the same response the payslips detail page already consumes) while the
// backend agent rolls out the enrichment fields.
// ---------------------------------------------------------------------------

interface TeacherEmbed {
  id: string;
  name: string;
  userId: string | null;
  status?: string;
}

interface ManagerEmbed {
  id: string;
  name: string;
  userId: string | null;
  status?: string;
}

interface PayeeUserEmbed {
  id: string;
  displayName: string | null;
  email: string | null;
}

interface PayeeEmbed {
  id: string;
  name: string;
  role: 'teacher' | 'manager';
  userId: string | null;
}

interface SalaryPaymentDetail {
  payment: SalaryPayment;
  cycle: SalaryCycle;
  teacher?: TeacherEmbed | null;
  manager?: ManagerEmbed | null;
  payee?: PayeeEmbed | null;
  payeeUser?: PayeeUserEmbed | null;
  approverName?: string | null;
  /** Additions such as the reporting incentive. */
  lines?: SalaryPaymentLineDto[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PAID_VIA_LABELS: Record<string, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  upi_offline: 'UPI (offline)',
  cheque: 'Cheque',
  other: 'Other',
};

const formatPaidVia = (via: string | null | undefined): string => {
  if (!via) return '—';
  return PAID_VIA_LABELS[via] ?? via;
};

const roleLabel = (role: 'teacher' | 'manager'): string =>
  role === 'teacher' ? 'Teacher' : 'Nalim';

// Backwards-compatible payee derivation — mirrors the payslip detail page so
// both surfaces render correctly while the enriched response rolls out.
const derivePayee = (data: SalaryPaymentDetail): PayeeEmbed | null => {
  if (data.payee) return data.payee;
  if (data.payment.role === 'teacher' && data.teacher) {
    return {
      id: data.teacher.id,
      name: data.teacher.name,
      role: 'teacher',
      userId: data.teacher.userId,
    };
  }
  if (data.payment.role === 'manager' && data.manager) {
    return {
      id: data.manager.id,
      name: data.manager.name,
      role: 'manager',
      userId: data.manager.userId,
    };
  }
  return null;
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function SalaryPaymentDetailPage(): JSX.Element {
  const { paymentId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const isAdmin = role === 'super_admin' || role === 'finance_admin';
  const isSuperAdmin = role === 'super_admin';

  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['salary-payment', 'detail', paymentId] as const,
    queryFn: ({ signal }) =>
      apiRequest<SalaryPaymentDetail>(
        `/api/salaries/payments/${encodeURIComponent(paymentId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const data = detailQuery.data;
  const payment = data?.payment ?? null;
  const cycle = data?.cycle ?? null;
  const payee = useMemo(() => (data ? derivePayee(data) : null), [data]);

  // Invalidate every query key that mirrors this payment so list, cycle and
  // payslip surfaces refresh as the operator clicks back.
  const invalidatePayment = (): void => {
    void queryClient.invalidateQueries({
      queryKey: ['salary-payment', 'detail', paymentId],
    });
    void queryClient.invalidateQueries({ queryKey: ['salaryPayments'] });
    if (cycle) {
      void queryClient.invalidateQueries({
        queryKey: ['salary-cycle', 'payments', cycle.id],
      });
      void queryClient.invalidateQueries({
        queryKey: ['salary-cycle', 'detail', cycle.id],
      });
    }
    void queryClient.invalidateQueries({
      queryKey: ['salary-payslip', 'detail', paymentId],
    });
  };

  const holdMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/salaries/payments/${encodeURIComponent(paymentId)}/hold`,
        { method: 'POST', body: { reason: 'Held by admin' } },
      ),
    onSuccess: () => {
      toast('Payment put on hold.', { variant: 'success' });
      invalidatePayment();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not hold payment.', {
        variant: 'error',
      });
    },
  });

  const releaseMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/salaries/payments/${encodeURIComponent(paymentId)}/release`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast('Payment released.', { variant: 'success' });
      invalidatePayment();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not release payment.', {
        variant: 'error',
      });
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/salaries/payments/${encodeURIComponent(paymentId)}/payslip/regenerate`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast('Payslip regeneration started.', { variant: 'success' });
      invalidatePayment();
    },
    onError: (err: unknown) => {
      toast(
        err instanceof ApiError ? err.message : 'Could not regenerate payslip.',
        { variant: 'error' },
      );
    },
  });

  const handleRegenerate = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Regenerate the payslip PDF?',
      description:
        'A new PDF will replace the existing one. The payslip number stays the same. Only super admins can perform this action.',
      confirmLabel: 'Regenerate',
    });
    if (!ok) return;
    regenerateMutation.mutate();
  };

  const openPayslipUrl = async (): Promise<void> => {
    try {
      await openApiDocument(`/api/salaries/payments/${encodeURIComponent(paymentId)}/payslip-url`);
    } catch (err) {
      if (err instanceof ApiError) toast(err.message, { variant: 'error' });
      else toast('Could not open payslip.', { variant: 'error' });
    }
  };

  // Build the SalaryMarkPaidDrawer's `joined` shape from the detail data —
  // the drawer was extracted from the list page and expects the joined view.
  const joinedForDrawer = useMemo<SalaryPaymentJoined | null>(() => {
    if (!payment) return null;
    const resolvedPayee = payee ?? {
      id:
        payment.role === 'teacher'
          ? payment.teacherId ?? ''
          : payment.managerId ?? '',
      name: '—',
      role: payment.role,
      userId: null,
    };
    return {
      payment,
      payee: {
        id: resolvedPayee.id,
        name: resolvedPayee.name,
        role: resolvedPayee.role,
      },
    };
  }, [payment, payee]);

  const isPaid = payment?.status === 'paid';
  const isHeld = payment?.status === 'held';
  const isPending = payment?.status === 'pending';

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!payment || !cycle) return [];
    return [
      { label: 'Gross', value: formatINR(payment.grossPaise) },
      {
        label: 'Deductions',
        value:
          payment.deductionsPaise > 0 ? formatINR(payment.deductionsPaise) : '—',
      },
      { label: 'Net', value: formatINR(payment.netPaise) },
      {
        label: payment.paidAt ? 'Paid on' : 'Pending since',
        value: payment.paidAt
          ? formatDate(payment.paidAt, { withTime: true })
          : formatDate(payment.createdAt, { withTime: true }),
      },
    ];
  }, [payment, cycle]);

  const title = useMemo(() => {
    if (!payee) return 'Salary payment';
    return payee.name;
  }, [payee]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={detailQuery.error}
        title="Salary payment"
        onRetry={() => detailQuery.refetch()}
      >
        {detailQuery.isLoading || !data || !payment || !cycle ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={title}
              leading={
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]">
                  <UserCircle size={22} aria-hidden="true" />
                </div>
              }
              sublabel={
                <>
                  {payee ? (
                    <span className="rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[color:var(--color-text-muted)]">
                      {roleLabel(payee.role)}
                    </span>
                  ) : null}
                  <span>
                    Salary for{' '}
                    <span className="font-medium text-[color:var(--color-text)]">
                      {cycleLabel(cycle.year, cycle.month)}
                    </span>
                  </span>
                  <span aria-hidden="true">·</span>
                  <span className="tabular-nums">
                    Payout {formatDate(`${cycle.payoutDate}T00:00:00Z`)}
                  </span>
                </>
              }
              statusChip={<SalaryStatusBadge status={payment.status} />}
              backTo="/finance/salaries"
              actions={
                <>
                  {/* Not for a ₹0 salary: set the amount first. */}
                  {isAdmin && isPending && payment.netPaise > 0 ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<CheckCircle2 size={14} aria-hidden="true" />}
                      onClick={() => setMarkPaidOpen(true)}
                    >
                      Mark paid
                    </Button>
                  ) : null}
                  {isPaid ? (
                    <Link
                      to="/finance/payslips/$paymentId"
                      params={{ paymentId: payment.id }}
                      className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-1.5 text-sm font-medium text-[color:var(--color-text)] hover:border-gold-500"
                    >
                      <FileText size={14} aria-hidden="true" />
                      View payslip
                    </Link>
                  ) : null}
                  {isSuperAdmin && isPaid ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<RefreshCw size={14} aria-hidden="true" />}
                      loading={regenerateMutation.isPending}
                      onClick={() => {
                        void handleRegenerate();
                      }}
                    >
                      Regenerate payslip
                    </Button>
                  ) : null}
                  {isAdmin && !isPaid ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Sliders size={14} aria-hidden="true" />}
                      onClick={() => setAdjustOpen(true)}
                    >
                      Adjust deductions
                    </Button>
                  ) : null}
                  {isAdmin && isPending ? (
                    <StatusTransitionButton
                      label="Hold"
                      icon={<Pause size={14} aria-hidden="true" />}
                      variant="danger"
                      size="sm"
                      confirmCopy={{
                        title: 'Hold this salary payment?',
                        description:
                          'The payment is frozen until released. The payee will not receive a payout while held.',
                        confirmLabel: 'Hold payment',
                        variant: 'danger',
                      }}
                      onConfirm={async () => {
                        await holdMutation.mutateAsync();
                      }}
                    />
                  ) : null}
                  {isAdmin && isHeld ? (
                    <StatusTransitionButton
                      label="Release"
                      icon={<Play size={14} aria-hidden="true" />}
                      variant="secondary"
                      size="sm"
                      confirmCopy={{
                        title: 'Release this salary payment?',
                        description:
                          'The payment returns to pending and can be marked paid again.',
                        confirmLabel: 'Release payment',
                      }}
                      onConfirm={async () => {
                        await releaseMutation.mutateAsync();
                      }}
                    />
                  ) : null}
                </>
              }
            />

            <KpiStrip items={kpiItems} />

            <div className="space-y-4">
              <PaymentDetailsCard payment={payment} lines={data?.lines ?? []} />
              <div className="grid gap-4 lg:grid-cols-2">
                <CycleCard cycle={cycle} />
                <PayeeCard payee={payee} payeeUser={data.payeeUser ?? null} />
              </div>
              <ApprovalCard
                payment={payment}
                approverName={data.approverName ?? null}
              />
              <PayslipCard
                payment={payment}
                onOpenPayslip={openPayslipUrl}
              />
            </div>

            <ActivityPanel
              targetType="salary_payment"
              targetId={payment.id}
              title="Activity"
            />
          </div>
        )}
      </GatedSection>

      {isAdmin && joinedForDrawer ? (
        <SalaryMarkPaidDrawer
          joined={markPaidOpen ? joinedForDrawer : null}
          onClose={() => setMarkPaidOpen(false)}
          onMarked={() => {
            setMarkPaidOpen(false);
            invalidatePayment();
            toast('Salary marked as paid.', { variant: 'success' });
          }}
        />
      ) : null}

      {isAdmin && payment && !isPaid ? (
        <AdjustSalaryAmountsDrawer
          payment={adjustOpen ? payment : null}
          onClose={() => setAdjustOpen(false)}
          onSaved={() => {
            setAdjustOpen(false);
            invalidatePayment();
            toast('Amounts updated.', { variant: 'success' });
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section / Field primitives — mirror the structure used in the fees payment
// detail and payslip detail pages so the visual language stays consistent.
// ---------------------------------------------------------------------------

interface SectionCardProps {
  title: string;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}

function SectionCard({
  title,
  children,
  action,
  className,
}: SectionCardProps): JSX.Element {
  return (
    <section
      className={clsx(
        'rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          {title}
        </h3>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <div className="mt-3 space-y-2 text-sm">{children}</div>
    </section>
  );
}

interface FieldProps {
  label: string;
  value?: ReactNode;
  children?: ReactNode;
  mono?: boolean;
  multiline?: boolean;
  highlight?: boolean;
}

function Field({
  label,
  value,
  children,
  mono = false,
  multiline = false,
  highlight = false,
}: FieldProps): JSX.Element {
  const content = children ?? value;
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <span className="text-xs text-[color:var(--color-text-muted)]">{label}</span>
      <span
        className={clsx(
          'min-w-0 text-right text-[color:var(--color-text)]',
          mono && 'font-mono text-xs',
          multiline && 'whitespace-pre-wrap text-left',
          highlight && 'font-semibold',
        )}
      >
        {content === null || content === undefined || content === '' ? '—' : content}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

function PaymentDetailsCard({ payment, lines }: { payment: SalaryPayment; lines: SalaryPaymentLineDto[] }): JSX.Element {
  return (
    <SectionCard title="Payment details">
      <Field label="Status">
        <SalaryStatusBadge status={payment.status} />
      </Field>
      <Field label="Gross" value={formatINR(payment.grossPaise)} />
      {lines.map((l) => (
        <Field key={l.id} label={l.label} value={`+ ${formatINR(l.amountPaise)}`} />
      ))}
      <Field
        label="Deductions"
        value={
          payment.deductionsPaise > 0 ? formatINR(payment.deductionsPaise) : '—'
        }
      />
      <Field label="Net" value={formatINR(payment.netPaise)} highlight />
      {payment.notes ? (
        <Field label="Notes" value={payment.notes} multiline />
      ) : null}
    </SectionCard>
  );
}

function CycleCard({ cycle }: { cycle: SalaryCycle }): JSX.Element {
  return (
    <SectionCard title="Salary cycle">
      <p className="text-sm font-medium text-[color:var(--color-text)]">
        {cycleLabel(cycle.year, cycle.month)}
      </p>
      <p className="text-xs text-[color:var(--color-text-muted)]">
        Payout {formatDate(`${cycle.payoutDate}T00:00:00Z`)}
      </p>
      {cycle.notes ? (
        <p className="mt-2 whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
          {cycle.notes}
        </p>
      ) : null}
      <div className="mt-3">
        <Link
          to="/finance/salaries/$cycleId"
          params={{ cycleId: cycle.id }}
          className="text-xs text-gold-700 underline hover:no-underline"
        >
          Open cycle
        </Link>
      </div>
    </SectionCard>
  );
}

interface PayeeCardProps {
  payee: PayeeEmbed | null;
  payeeUser: PayeeUserEmbed | null;
}

function PayeeCard({ payee, payeeUser }: PayeeCardProps): JSX.Element {
  if (!payee) {
    return (
      <SectionCard title="Payee">
        <p className="text-sm text-[color:var(--color-text-muted)]">
          Payee details unavailable.
        </p>
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Payee">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]">
          <UserCircle size={22} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          {payee.role === 'teacher' ? (
            <Link
              to="/people/teachers/$teacherId"
              params={{ teacherId: payee.id }}
              className="text-sm font-medium text-[color:var(--color-text)] hover:underline"
            >
              {payee.name}
            </Link>
          ) : (
            <Link
              to="/people/managers/$managerId"
              params={{ managerId: payee.id }}
              className="text-sm font-medium text-[color:var(--color-text)] hover:underline"
            >
              {payee.name}
            </Link>
          )}
          <p className="text-xs text-[color:var(--color-text-muted)]">
            {roleLabel(payee.role)}
          </p>
          {payeeUser ? (
            <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
              {payeeUser.email ?? payeeUser.displayName ?? '—'}
            </p>
          ) : null}
          {payee.userId ? (
            <div className="mt-2">
              <Link
                to="/system/users/$userId"
                params={{ userId: payee.userId }}
                className="text-xs text-gold-700 underline hover:no-underline"
              >
                View login
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </SectionCard>
  );
}

interface ApprovalCardProps {
  payment: SalaryPayment;
  approverName: string | null;
}

function ApprovalCard({ payment, approverName }: ApprovalCardProps): JSX.Element {
  if (payment.status !== 'paid') {
    return (
      <SectionCard title="Approval">
        <p className="text-sm text-[color:var(--color-text-muted)]">
          Approval details will appear once the payment is marked paid.
        </p>
      </SectionCard>
    );
  }
  const fallbackApprover = payment.approvedBy ? `User ${payment.approvedBy}` : '—';
  return (
    <SectionCard title="Approval">
      <Field label="Approved by" value={approverName ?? fallbackApprover} />
      <Field
        label="Paid at"
        value={
          payment.paidAt
            ? formatDate(payment.paidAt, { withTime: true })
            : '—'
        }
      />
      <Field label="Paid via" value={formatPaidVia(payment.paidVia)} />
      <Field
        label="Reference"
        value={
          payment.paidReference ? (
            <span className="font-mono text-xs">{payment.paidReference}</span>
          ) : (
            '—'
          )
        }
        mono
      />
    </SectionCard>
  );
}

interface PayslipCardProps {
  payment: SalaryPayment;
  onOpenPayslip: () => Promise<void>;
}

function PayslipCard({ payment, onOpenPayslip }: PayslipCardProps): JSX.Element {
  const isPaid = payment.status === 'paid';
  return (
    <SectionCard title="Payslip">
      <p className="text-sm font-medium text-[color:var(--color-text)]">
        {payment.payslipNumber ?? '—'}
      </p>
      {!isPaid ? (
        <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
          Payslips become available once the payment is marked paid.
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            icon={<FileText size={14} aria-hidden="true" />}
            onClick={() => {
              void onOpenPayslip();
            }}
          >
            Download PDF
          </Button>
          <Link
            to="/finance/payslips/$paymentId"
            params={{ paymentId: payment.id }}
            className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-text)] hover:border-gold-500"
          >
            <FileText size={14} aria-hidden="true" />
            Open payslip detail
          </Link>
        </div>
      )}
    </SectionCard>
  );
}
